import { useEffect, useState, type ReactNode } from 'react';
import Animated, {
  Easing,
  runOnJS,
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';

import { View } from '@/components/ui/view';
import { DURATION } from '@/constants/animation';

/** How far the terms rise into place as they appear, in points. */
const RISE_DISTANCE = 8;

const EASE_OUT = Easing.out(Easing.cubic);

/**
 * Opens and closes the composer's terms row. Opening grows the row to its
 * measured height while the content fades in and rises into place. Closing
 * fades the content first and then folds the row away, so the composer shrinks
 * after its terms have gone rather than squeezing text that is still visible.
 *
 * Driven by shared values rather than layout animations, which do not run on
 * web. The content is laid out absolutely inside the row, because a row held
 * at zero height squeezes an in-flow child to zero on native, and the content
 * could then never be measured to open to. With reduced motion the row snaps between states. The content stays
 * mounted only until it has closed, and is hidden from assistive technology
 * while closing.
 */
export function ComposerTermsReveal({
  isVisible,
  children,
}: {
  isVisible: boolean;
  children: ReactNode;
}) {
  const prefersReducedMotion = useReducedMotion();

  const [isMounted, setIsMounted] = useState(isVisible);

  const [contentHeight, setContentHeight] = useState(0);

  const progress = useSharedValue(isVisible ? 1 : 0);

  const height = useSharedValue(isVisible ? -1 : 0);

  if (isVisible && !isMounted) {
    setIsMounted(true);
  }

  useEffect(() => {
    if (!isMounted || contentHeight === 0) {
      return;
    }

    if (prefersReducedMotion || (isVisible && height.value < 0)) {
      progress.value = isVisible ? 1 : 0;

      height.value = isVisible ? contentHeight : 0;

      if (!isVisible) {
        setIsMounted(false);
      }

      return;
    }

    if (isVisible) {
      height.value = withTiming(contentHeight, { duration: DURATION.normal, easing: EASE_OUT });

      progress.value = withTiming(1, { duration: DURATION.normal, easing: EASE_OUT });

      return;
    }

    progress.value = withTiming(0, { duration: DURATION.fast, easing: EASE_OUT });

    height.value = withTiming(0, { duration: DURATION.normal, easing: EASE_OUT }, (finished) => {
      if (finished) {
        runOnJS(setIsMounted)(false);
      }
    });
  }, [isVisible, isMounted, contentHeight, prefersReducedMotion, progress, height]);

  const rowStyle = useAnimatedStyle(() =>
    height.value < 0 ? {} : { height: height.value, overflow: 'hidden' }
  );

  const contentStyle = useAnimatedStyle(() => ({
    position: height.value < 0 ? 'relative' : 'absolute',
    top: 0,
    left: 0,
    right: 0,
    opacity: progress.value,
    transform: [{ translateY: (1 - progress.value) * RISE_DISTANCE }],
  }));

  if (!isMounted) {
    return null;
  }

  return (
    <Animated.View
      style={rowStyle}
      pointerEvents={isVisible ? 'auto' : 'none'}
      aria-hidden={!isVisible}
      importantForAccessibility={isVisible ? 'auto' : 'no-hide-descendants'}>
      <Animated.View style={contentStyle}>
        <View
          onLayout={(event) => {
            const measured = event.nativeEvent.layout.height;

            if (measured > 0 && measured !== contentHeight) {
              setContentHeight(measured);
            }
          }}>
          {children}
        </View>
      </Animated.View>
    </Animated.View>
  );
}
